'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';

import { useCurrency } from '@/hooks/useCurrency';

import { MonthlyCost } from '../engine';

type Props = {
  baseline: MonthlyCost[];
  scenario?: MonthlyCost[];
};

const MONTH_INITIALS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const VIEW_W = 360;
const VIEW_H = 180;
const PLOT_H = 160;
const LABEL_Y = 176;
const GROUP_W = VIEW_W / 12;
const BAR_GAP = 2;
const BAR_RADIUS = 2;

// Named theme tokens (per design review): electricity = orange, gas/fuel =
// maroon. Defined once in DesignSystemProvider so the palette lives in one place.
const ELECTRIC_COLOR = 'uiChartElectric';
const GAS_COLOR = 'uiChartGas';

// "Today" bars are dimmed so the brighter "with your plan" bars read as the
// recommendation; only meaningful when both series are shown side by side.
const BASELINE_OPACITY = 0.5;

// Below this monthly gas/fuel cost the maroon segment (and its legend chip and
// table column) is hidden. Dollars — the same gate for bars, legend, and table.
const FOSSIL_MIN_USD = 0.5;

const hasFossil = (months?: MonthlyCost[]) => months?.some(({ fossilUsd }) => fossilUsd > FOSSIL_MIN_USD) ?? false;

const LegendChip = ({ color, label }: { color: string; label: string }) => (
  <DS.Flex alignItems="center" flexDirection="row" gap={1}>
    <x.span bg={color} borderRadius="full" display="inline-block" h={3} w={3} />
    <DS.Text color="uiHelperCopy" text="body12">
      {label}
    </DS.Text>
  </DS.Flex>
);

type StackedBarProps = {
  electricH: number;
  fossilH: number;
  opacity: number;
  showFossil: boolean;
  width: number;
  x: number;
};

// Bar with only its top two corners rounded — keeps the stacked seam and the
// baseline square (per design review). Radius is capped at h/2 so very short
// segments keep straight sides instead of collapsing into a semicircular cap.
const roundedTopRectPath = (bx: number, by: number, w: number, h: number): string => {
  const r = Math.min(BAR_RADIUS, w / 2, h / 2);
  return `M${bx},${by + h} L${bx},${by + r} Q${bx},${by} ${bx + r},${by} L${bx + w - r},${by} Q${bx + w},${by} ${bx + w},${by + r} L${bx + w},${by + h} Z`;
};

// Electricity (orange) on the bottom, gas/fuel (maroon) stacked on top — the
// same split for both the baseline ("Today") and scenario ("With your plan")
// bars. `showFossil` is decided in dollars by the caller so the bar, legend,
// and table agree. Only the top of the whole bar is rounded.
const StackedBar = ({ electricH, fossilH, opacity, showFossil, width, x: barX }: StackedBarProps) => {
  const electricHeight = Math.max(electricH, 1);
  const electricTop = PLOT_H - electricHeight;

  return (
    <>
      {showFossil ? (
        <x.rect
          fill={ELECTRIC_COLOR}
          height={electricHeight}
          opacity={opacity}
          width={width}
          x={barX}
          y={electricTop}
        />
      ) : (
        <x.path
          d={roundedTopRectPath(barX, electricTop, width, electricHeight)}
          fill={ELECTRIC_COLOR}
          opacity={opacity}
        />
      )}
      {showFossil ? (
        <x.path
          d={roundedTopRectPath(barX, electricTop - fossilH, width, fossilH)}
          fill={GAS_COLOR}
          opacity={opacity}
        />
      ) : null}
    </>
  );
};

export const BillChart = ({ baseline, scenario }: Props) => {
  const { formatCurrency } = useCurrency();
  const colors = DS.useChartColors();

  const hasScenario = scenario !== undefined;
  const anyFossil = hasFossil(baseline) || hasFossil(scenario);
  const maxUsd = Math.max(
    ...baseline.map(({ totalUsd }) => totalUsd),
    ...(scenario?.map(({ totalUsd }) => totalUsd) ?? []),
    1
  );
  const scaleY = (usd: number) => (usd / maxUsd) * (PLOT_H - 8);

  const barWidth = hasScenario ? (GROUP_W - BAR_GAP * 3) / 2 : GROUP_W / 2;
  const baselineOpacity = hasScenario ? BASELINE_OPACITY : 1;

  return (
    <DS.Flex as="figure" gap={3} m={0} maxW="720px" mx="auto" w="100%">
      <DS.Flex gap={2}>
        <DS.Flex flexDirection="row" flexWrap="wrap" gap={4} justifyContent="center">
          <LegendChip color={ELECTRIC_COLOR} label="Electricity" />
          {anyFossil ? <LegendChip color={GAS_COLOR} label="Gas and fuel" /> : null}
        </DS.Flex>
        {hasScenario ? (
          <DS.Text color="uiHelperCopy" text="body12" textAlign="center">
            Lighter bars are today’s costs; solid bars are with your plan.
          </DS.Text>
        ) : null}
      </DS.Flex>

      <x.svg
        aria-label="Monthly energy costs, before and after your plan"
        role="img"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        w="100%"
      >
        {baseline.map(({ electricUsd, fossilUsd, month }) => {
          const groupX = month * GROUP_W;
          const baselineX = hasScenario ? groupX + BAR_GAP : groupX + GROUP_W / 4;
          const scenarioMonth = scenario?.[month];
          const scenarioX = groupX + BAR_GAP * 2 + barWidth;

          return (
            <g key={month}>
              <StackedBar
                electricH={scaleY(electricUsd)}
                fossilH={scaleY(fossilUsd)}
                opacity={baselineOpacity}
                showFossil={fossilUsd > FOSSIL_MIN_USD}
                width={barWidth}
                x={baselineX}
              />
              {scenarioMonth ? (
                <StackedBar
                  electricH={scaleY(scenarioMonth.electricUsd)}
                  fossilH={scaleY(scenarioMonth.fossilUsd)}
                  opacity={1}
                  showFossil={scenarioMonth.fossilUsd > FOSSIL_MIN_USD}
                  width={barWidth}
                  x={scenarioX}
                />
              ) : null}
              <x.text fill={colors.tickLabel} fontSize="9px" textAnchor="middle" x={groupX + GROUP_W / 2} y={LABEL_Y}>
                {MONTH_INITIALS[month]}
              </x.text>
            </g>
          );
        })}
      </x.svg>

      {/* Crawlable, screen-reader-friendly data behind the visualization.
          Inline style: the canonical visually-hidden recipe — xstyled w/h props
          don't constrain a <table> (table layout ignores width:1px) and clipPath
          isn't emitted, so the clip rect is the only reliable way to hide it. */}
      <table
        style={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px',
        }}
      >
        <caption>Estimated monthly energy costs</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col">Today: electricity</th>
            {anyFossil ? <th scope="col">Today: gas and fuel</th> : null}
            {hasScenario ? <th scope="col">With your plan: electricity</th> : null}
            {hasScenario && anyFossil ? <th scope="col">With your plan: gas and fuel</th> : null}
          </tr>
        </thead>
        <tbody>
          {baseline.map(({ electricUsd, fossilUsd, month }) => (
            <tr key={month}>
              <th scope="row">{MONTH_NAMES[month]}</th>
              <td>{formatCurrency(Math.round(electricUsd))}</td>
              {anyFossil ? <td>{formatCurrency(Math.round(fossilUsd))}</td> : null}
              {hasScenario ? <td>{formatCurrency(Math.round(scenario[month].electricUsd))}</td> : null}
              {hasScenario && anyFossil ? <td>{formatCurrency(Math.round(scenario[month].fossilUsd))}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </DS.Flex>
  );
};
